// ===================================================================
// services/xpService.ts
// Envoie chaque gain d'XP au serveur (fonction Supabase award_xp, migration
// 2026-10-24_xp_events.sql), qui le valide, l'inscrit dans le journal
// xp_events et met à jour user_profiles.lifetime_xp (XP cumulée toutes
// langues).
//
// Les gains passent par une file en localStorage : un gain fait hors ligne
// ou en invité est envoyé plus tard, à la reconnexion. Chaque gain porte un
// identifiant unique : le renvoyer après une coupure ne le compte pas deux fois.
// ===================================================================
import { supabase } from './supabaseAudioService';

const QUEUE_KEY = 'jogolinga-xp-queue';
const LIFETIME_KEY = 'jogolinga-lifetime-xp';
const MAX_QUEUE = 200;

/** Activités acceptées par award_xp (les autres sont ignorées côté app). */
export const XP_SOURCES = ['learn', 'grammar', 'sentences', 'challenge', 'memoryDuel', 'wordle', 'forge', 'weaver', 'onboarding'] as const;
export type XpSource = typeof XP_SOURCES[number];

interface QueuedAward {
  id: string;
  language: string;
  source: XpSource;
  amount: number;
}

function newId(): string {
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof (c as any).randomUUID === 'function') return (c as any).randomUUID();
  // Repli pour les WebViews anciennes : UUID v4 à partir de getRandomValues
  const b = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function readQueue(): QueuedAward[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedAward[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE)));
  } catch { /* stockage plein : tant pis pour ce gain */ }
}

/** Dernier lifetime_xp connu (renvoyé par le serveur), ou null. */
export function getCachedLifetimeXP(): number | null {
  const v = localStorage.getItem(LIFETIME_KEY);
  return v === null ? null : Number(v);
}

let flushing = false;

/**
 * Envoie la file au serveur, dans l'ordre. S'arrête à la première erreur
 * réseau ou d'authentification (nouvel essai plus tard) ; un gain refusé
 * pour une raison définitive (activité inconnue, montant invalide) est retiré.
 */
export async function flushXPQueue(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return; // invité : on garde la file pour plus tard

    let queue = readQueue();
    while (queue.length > 0) {
      const a = queue[0];
      const { data: total, error } = await supabase.rpc('award_xp', {
        p_event_id: a.id,
        p_language: a.language,
        p_source: a.source,
        p_amount: a.amount,
      });
      if (error) {
        const definitive = /invalid_(source|amount|language|event)/.test(error.message || '');
        // Fonction absente (migration pas encore passée) ou réseau : on garde tout
        if (!definitive) return;
      } else if (typeof total === 'number') {
        localStorage.setItem(LIFETIME_KEY, String(total));
      }
      queue = readQueue().filter(q => q.id !== a.id);
      writeQueue(queue);
    }
  } catch {
    /* hors ligne : nouvel essai au prochain gain ou à la reconnexion */
  } finally {
    flushing = false;
  }
}

/** Enregistre un gain et tente de l'envoyer tout de suite. */
export function awardXP(language: string, source: XpSource, amount: number): void {
  if (!language || !(amount > 0) || !XP_SOURCES.includes(source)) return;
  writeQueue([...readQueue(), { id: newId(), language, source, amount: Math.round(amount) }]);
  flushXPQueue();
}

/** À la déconnexion : le total en cache appartient au compte qui part. */
export function clearCachedLifetimeXP(): void {
  localStorage.removeItem(LIFETIME_KEY);
}
